import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Site, FortnoxReconciliationSettings as FortnoxReconciliationSettingsType } from '@/types';
import { apiService } from '@/utils/api';

interface FortnoxReconciliationSettingsProps {
  className?: string;
}

const FortnoxReconciliation: React.FC<FortnoxReconciliationSettingsProps> = ({ className }) => {
  const queryClient = useQueryClient();
  const [selectedSiteId, setSelectedSiteId] = useState<number | null>(null);
  const [formData, setFormData] = useState<Partial<FortnoxReconciliationSettingsType>>({});
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);

  // Fetch sites
  const { data: sites = [], isLoading: sitesLoading } = useQuery(
    ['sites'],
    () => apiService.getSites()
  );

  // Set default site when sites are loaded
  useEffect(() => {
    if (sites.length > 0 && !selectedSiteId) {
      setSelectedSiteId(sites[0].id);
    }
  }, [sites, selectedSiteId]);

  // Fetch Fortnox reconciliation settings
  const { data: reconciliationSettings, isLoading: settingsLoading } = useQuery(
    ['fortnox-reconciliation-settings', selectedSiteId],
    () => apiService.getFortnoxReconciliationSettings(selectedSiteId!),
    {
      enabled: !!selectedSiteId,
    }
  );

  // Update form data when settings are loaded
  useEffect(() => {
    if (reconciliationSettings) {
      setFormData(reconciliationSettings);
    }
  }, [reconciliationSettings]);

  // Save mutation
  const saveMutation = useMutation(
    async (data: FortnoxReconciliationSettingsType) => {
      if (data.id) {
        return apiService.updateFortnoxReconciliationSettings(selectedSiteId!, data);
      } else {
        return apiService.createFortnoxReconciliationSettings(selectedSiteId!, data);
      }
    },
    {
      onSuccess: () => {
        setMessage({ type: 'success', text: 'Fortnox Reconciliation Settings saved' });
        queryClient.invalidateQueries(['fortnox-reconciliation-settings']);
        setTimeout(() => setMessage(null), 3000);
      },
      onError: () => {
        setMessage({ type: 'error', text: 'Something went wrong' });
        setTimeout(() => setMessage(null), 3000);
      },
    }
  );

  // Delete mutation
  const deleteMutation = useMutation(
    async () => {
      return apiService.deleteFortnoxReconciliationSettings(selectedSiteId!);
    },
    {
      onSuccess: () => {
        setMessage({ type: 'success', text: 'Fortnox Reconciliation Settings deleted' });
        setFormData({});
        queryClient.invalidateQueries(['fortnox-reconciliation-settings']);
        setTimeout(() => setMessage(null), 3000);
      },
      onError: () => {
        setMessage({ type: 'error', text: 'Something went wrong' });
        setTimeout(() => setMessage(null), 3000);
      },
    }
  );

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSiteChange = (siteId: number) => {
    setSelectedSiteId(siteId);
    setFormData({});
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedSiteId) return;

    const submitData: FortnoxReconciliationSettingsType = {
      ...formData,
      site_id: selectedSiteId,
    } as FortnoxReconciliationSettingsType;

    saveMutation.mutate(submitData);
  };

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this integration?')) {
      deleteMutation.mutate();
    }
  };

  const isLoading = sitesLoading || settingsLoading;

  return (
    <div className={`container mx-auto px-4 py-8 ${className || ''}`}>
      <h1 className="section-title">Fortnox Reconciliation Settings</h1>

      {/* Loading Alert */}
      {isLoading && (
        <div className="alert alert-info">
          <span>Loading...</span>
        </div>
      )}

      {/* Success/Error Messages */}
      {message && (
        <div className={`alert ${message.type === 'success' ? 'alert-success' : message.type === 'error' ? 'alert-danger' : 'alert-info'}`}>
          <span>{message.text}</span>
        </div>
      )}

      <div className="card">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Site Selection */}
          <div>
            <label className="form-label">Site ID:</label>
            <select
              value={selectedSiteId || ''}
              onChange={(e) => handleSiteChange(parseInt(e.target.value))}
              className="form-input"
              disabled={isLoading}
            >
              {sites.map((site: Site) => (
                <option key={site.id} value={site.id}>
                  {site.url}
                </option>
              ))}
            </select>
          </div>

          {/* Account Settings Row 1 */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <label className="form-label form-label-required">Invoice Payment Account:</label>
              <input
                type="number"
                value={formData.invoice_payment_account || ''}
                onChange={(e) => handleInputChange('invoice_payment_account', parseInt(e.target.value) || '')}
                className="form-input"
                disabled={isLoading}
                required
              />
            </div>

            <div>
              <label className="form-label form-label-required">Financial Fee Account:</label>
              <input
                type="number"
                value={formData.financial_fee_account || ''}
                onChange={(e) => handleInputChange('financial_fee_account', parseInt(e.target.value) || '')}
                className="form-input"
                disabled={isLoading}
                required
              />
            </div>

            <div>
              <label className="form-label">Freight Account:</label>
              <input
                type="number"
                value={formData.freight_account || ''}
                onChange={(e) => handleInputChange('freight_account', parseInt(e.target.value) || '')}
                className="form-input"
                disabled={isLoading}
              />
            </div>

            <div>
              <label className="form-label">Penny Equalization Account:</label>
              <input
                type="number"
                value={formData.penny_equalization_account || ''}
                onChange={(e) => handleInputChange('penny_equalization_account', parseInt(e.target.value) || '')}
                className="form-input"
                disabled={isLoading}
              />
            </div>

            <div>
              <label className="form-label form-label-required">Fee VAT Account:</label>
              <input
                type="number"
                value={formData.fee_vat_account || ''}
                onChange={(e) => handleInputChange('fee_vat_account', parseInt(e.target.value) || '')}
                className="form-input"
                disabled={isLoading}
                required
              />
            </div>

            <div>
              <label className="form-label">Customer Debt Account:</label>
              <input
                type="number"
                value={formData.customer_debt_account || ''}
                onChange={(e) => handleInputChange('customer_debt_account', parseInt(e.target.value) || '')}
                className="form-input"
                disabled={isLoading}
              />
            </div>

            <div>
              <label className="form-label">Report recipient:</label>
              <input
                type="email"
                value={formData.report_recipient || ''}
                onChange={(e) => handleInputChange('report_recipient', e.target.value)}
                className="form-input"
                placeholder="Enter email"
                disabled={isLoading}
              />
            </div>
          </div>

          {/* VAT Settings Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <label className="form-label">Sales Account 25%:</label>
              <input
                type="number"
                value={formData.sales_vat_25_account || ''}
                onChange={(e) => handleInputChange('sales_vat_25_account', parseInt(e.target.value) || '')}
                className="form-input"
                disabled={isLoading}
              />
            </div>

            <div>
              <label className="form-label">Sales Account 12%:</label>
              <input
                type="number"
                value={formData.sales_vat_12_account || ''}
                onChange={(e) => handleInputChange('sales_vat_12_account', parseInt(e.target.value) || '')}
                className="form-input"
                disabled={isLoading}
              />
            </div>

            <div>
              <label className="form-label">Sales Account 6%:</label>
              <input
                type="number"
                value={formData.sales_vat_6_account || ''}
                onChange={(e) => handleInputChange('sales_vat_6_account', parseInt(e.target.value) || '')}
                className="form-input"
                disabled={isLoading}
              />
            </div>

            <div>
              <label className="form-label">VAT Account 25%:</label>
              <input
                type="number"
                value={formData.vat_25_account || ''}
                onChange={(e) => handleInputChange('vat_25_account', parseInt(e.target.value) || '')}
                className="form-input"
                disabled={isLoading}
              />
            </div>

            <div>
              <label className="form-label">VAT Account 12%:</label>
              <input
                type="number"
                value={formData.vat_12_account || ''}
                onChange={(e) => handleInputChange('vat_12_account', parseInt(e.target.value) || '')}
                className="form-input"
                disabled={isLoading}
              />
            </div>

            <div>
              <label className="form-label">VAT Account 6%:</label>
              <input
                type="number"
                value={formData.vat_6_account || ''}
                onChange={(e) => handleInputChange('vat_6_account', parseInt(e.target.value) || '')}
                className="form-input"
                disabled={isLoading}
              />
            </div>

            <div>
              <label className="form-label">Default VAT Rate:</label>
              <input
                type="number"
                value={formData.default_vat_rate || ''}
                onChange={(e) => handleInputChange('default_vat_rate', parseFloat(e.target.value) || '')}
                className="form-input"
                disabled={isLoading}
              />
            </div>
          </div>

          {/* Options Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                id="convert_currency"
                checked={formData.convert_currency || false}
                onChange={(e) => handleInputChange('convert_currency', e.target.checked)}
                className="w-5 h-5 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                disabled={isLoading}
              />
              <label htmlFor="convert_currency" className="text-sm font-medium text-gray-700">
                Convert currency
              </label>
            </div>

            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                id="round_amount"
                checked={formData.round_amount || false}
                onChange={(e) => handleInputChange('round_amount', e.target.checked)}
                className="w-5 h-5 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                disabled={isLoading}
              />
              <label htmlFor="round_amount" className="text-sm font-medium text-gray-700">
                Round amount
              </label>
            </div>

            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                id="oss_enabled"
                checked={formData.oss_enabled || false}
                onChange={(e) => handleInputChange('oss_enabled', e.target.checked)}
                className="w-5 h-5 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                disabled={isLoading}
              />
              <label htmlFor="oss_enabled" className="text-sm font-medium text-gray-700">
                OSS Enabled
              </label>
            </div>

            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                id="book_payment_only"
                checked={formData.book_payment_only || false}
                onChange={(e) => handleInputChange('book_payment_only', e.target.checked)}
                className="w-5 h-5 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                disabled={isLoading}
              />
              <label htmlFor="book_payment_only" className="text-sm font-medium text-gray-700">
                Book payment only
              </label>
            </div>

            <div>
              <label className="form-label">Voucher Series:</label>
              <input
                type="text"
                value={formData.voucher_series || ''}
                onChange={(e) => handleInputChange('voucher_series', e.target.value)}
                className="form-input"
                disabled={isLoading}
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end pt-6 space-x-3">
            <button
              type="submit"
              className="px-4 py-2 text-white rounded-md hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50"
              style={{ backgroundColor: 'hsl(15 100% 52%)' }}
              disabled={isLoading || saveMutation.isLoading}
            >
              {saveMutation.isLoading ? 'Saving...' : 'Save'}
            </button>

            {formData.id && (
              <button
                type="button"
                onClick={handleDelete}
                className="btn btn-danger"
                disabled={isLoading || deleteMutation.isLoading}
              >
                {deleteMutation.isLoading ? 'Deleting...' : 'Delete'}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default FortnoxReconciliation;
